"use client";

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CommunityPost } from '@/types';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { useToast } from '@/hooks/useToast';

interface EditPostModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  post: CommunityPost;
  onPostUpdated?: (updatedPost: CommunityPost) => void;
}

export default function EditPostModal({
  open,
  onOpenChange,
  post,
  onPostUpdated
}: EditPostModalProps) {
  const { user } = useAuthContext();
  const { toast } = useToast();
  const [content, setContent] = useState(post.content);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update content when post changes
  useEffect(() => {
    if (open) {
      setContent(post.content);
    }
  }, [open, post.content]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || !user || isSubmitting) return;

    // Capture scroll position before the modal closes — Radix Dialog tries
    // to restore focus on close, and when the parent refetches the posts
    // list the original trigger element is no longer in the DOM, which
    // sometimes causes the page to scroll to the document end. Manually
    // restoring scrollY on the next two frames undoes that jump.
    const savedScrollY = typeof window !== 'undefined' ? window.scrollY : 0;
    const restoreScroll = () => {
      if (typeof window === 'undefined') return;
      requestAnimationFrame(() => {
        window.scrollTo(0, savedScrollY);
        requestAnimationFrame(() => window.scrollTo(0, savedScrollY));
      });
    };

    setIsSubmitting(true);
    try {
      const response = await fetch(`/api/community/posts/${post.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id
        },
        body: JSON.stringify({
          content: content.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || 'Failed to update post');
      }

      const updatedPost = await response.json();
      onPostUpdated?.(updatedPost);
      onOpenChange(false);
      restoreScroll();

      toast({
        title: "Post updated",
        description: "Your post has been updated successfully",
        variant: "success",
      });
    } catch (error) {
      console.error('Error updating post:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update post. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface-2 border-border-subtle text-text-primary sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="text-text-primary">Edit Post</DialogTitle>
          <DialogDescription className="text-text-tertiary">
            Update your post content
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What's on your mind?"
            className="bg-surface-3 border-border-default text-text-primary placeholder:text-text-tertiary min-h-[150px] resize-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                handleSubmit(e);
              }
            }}
          />
          <div className="flex justify-between items-center">
            <p className="text-xs text-text-tertiary">
              Press Ctrl+Enter to save
            </p>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="border-border-default text-text-secondary hover:bg-surface-3"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!content.trim() || isSubmitting}
                className="bg-accent hover:bg-accent-hover text-text-on-accent"
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

